import {
  Grid,
  GridCellProps,
  GridColumn,
  GridItemChangeEvent,
  GridNoRecords,
  GridRowProps
} from '@progress/kendo-react-grid';
import { LoadingGridPanel } from '../../grid/LoadingGridPanel';
import { DragAndDrop } from '@progress/kendo-react-common';
import React, { useEffect, useRef, useState } from 'react';
import { HeaderCell } from '../../grid/customCells/HeaderCell';
import { EDITING_VIEW_FIELD, EditingViewField } from '@cs-cms/types';
import { ViewGridType, ViewGridTypeEnum } from './ViewGridType';
import { DragCell } from '../../grid/customCells/DragCell';
import { HiddenHeader } from '../../grid/HiddenHeader';
import { EditDescriptor } from '@progress/kendo-react-data-tools';
import { EditableFacetsCountCell } from './userViews/privateView/EditableFacetsCountCell';
import { useGlobalizationContext, useTranslation } from '@cs-cms/state-management';
import { ElementDimensions, useWindowDimensions } from '../../hooks/useWindowDimensions';

interface ViewGridProps {
  type: ViewGridType;
  data: EditingViewField[];
  cellRender: (cellProps: GridCellProps) => React.JSX.Element | null;
  rowRender?: (
    row: React.ReactElement<HTMLTableRowElement, string | React.JSXElementConstructor<any>>,
    props: GridRowProps
  ) => React.ReactNode;
  isLoading: boolean;
  onChange?: (updatedData: EditingViewField[]) => void;
}

export const ViewGrid: React.FC<ViewGridProps> = props => {
  const { selectedLocale } = useGlobalizationContext();
  const { translate } = useTranslation(selectedLocale);
  const ref = useRef<HTMLDivElement>(null);
  const [gridDimensions, setGridDimensions] = useState<ElementDimensions>({ width: 0, height: 0 });
  const windowDimensions = useWindowDimensions();

  useEffect(() => {
    if (ref && ref.current) {
      setGridDimensions({
        height: 0, // workaround to reset grid height to shrink whole page body before recalculating the new grid height
        width: ref.current.getBoundingClientRect().width
      });
    }
  }, [windowDimensions]);

  useEffect(() => {
    if (ref && ref.current && gridDimensions.height === 0) {
      const paddingSize = 50;
      setGridDimensions({
        height: document.body.offsetHeight - ref.current.getBoundingClientRect().top - paddingSize,
        width: ref.current.getBoundingClientRect().width
      });
    }
  }, [gridDimensions]);

  const edit: EditDescriptor = {};
  props.data.forEach(item => {
    edit[item.id] = true;
  });

  const handleItemChange = (event: GridItemChangeEvent) => {
    const { field, value, dataIndex } = event;
    if (!props.onChange || !field) {
      return;
    }

    const updatedData = [...props.data];
    updatedData[dataIndex] = {
      ...updatedData[dataIndex],
      [field]: value
    };
    props.onChange(updatedData);
  };

  const gridContent: React.JSX.Element = (
    <>
      <Grid
        data={props.data}
        dataItemKey={'id'}
        style={{
          ...(props.isLoading && { minHeight: 500 }),
          height: gridDimensions.height
        }}
        cells={{ headerCell: HeaderCell }}
        size={props.type === ViewGridTypeEnum.READ_ONLY_VIEW ? 'medium' : 'small'}
        className="viewGrid"
        edit={edit}
        editable={{ mode: 'inline' }}
        onItemChange={handleItemChange}
      >
        <GridNoRecords>{translate('custom.common.emptyGridMessage')}</GridNoRecords>
        <GridColumn
          title={translate('custom.settings.views.gridHeader.order')}
          width={'120px'}
          cells={{ data: DragCell }}
        />
        <GridColumn
          field={EDITING_VIEW_FIELD.NAME}
          title={translate('custom.settings.views.gridHeader.name')}
          cells={{ data: props.cellRender }}
        />
        <GridColumn
          field={EDITING_VIEW_FIELD.WIDTH}
          title={translate('custom.settings.views.gridHeader.width')}
          editor="numeric"
        />
        {props.type !== ViewGridTypeEnum.READ_ONLY_VIEW && (
          <GridColumn
            field={EDITING_VIEW_FIELD.FACETS_COUNT}
            title={translate('custom.settings.views.gridHeader.facetsCount')}
            cells={{ data: EditableFacetsCountCell }}
          />
        )}
        <GridColumn
          field={EDITING_VIEW_FIELD.LINK}
          title={translate('custom.settings.views.gridHeader.isLink')}
          cells={{ data: props.cellRender }}
        />
        <GridColumn field="remove" width={'100px'} cells={{ data: props.cellRender, headerCell: HiddenHeader }} />
      </Grid>
      {props.isLoading && <LoadingGridPanel />}
    </>
  );

  return (
    <div ref={ref}>
      {props.type !== ViewGridTypeEnum.READ_ONLY_VIEW ? <DragAndDrop>{gridContent}</DragAndDrop> : gridContent}
    </div>
  );
};
